import cx from 'classnames'
import {InputProvider} from 'components/common/CartItemBuilder/providers/InputProvider'
import {ReactComponent as ClockIcon} from 'icons/clock.svg'
import moment from 'moment'
import {EnvironmentProvider} from 'providers/EnvironmentProvider'
import {formatAvailabilityId} from 'utils/formatAvailabilityId'
import {formatMoney} from 'utils/formatMoney'
import {VentrataDataProvider} from '../../providers/VentrataDataProvider'

type Props = {
  className?: string
}

export const Preview = ({className}: Props) => {
  const product = VentrataDataProvider.useProduct('optional')
  const currency = EnvironmentProvider.useCurrency()
  const optionId = InputProvider.useOptionId('optional')
  const date = InputProvider.useDate('optional')
  const availabilityId = InputProvider.useAvailabilityId('optional')
  const counters = InputProvider.useUnitCounters('optional')
  const units = VentrataDataProvider.useUnits('optional')
  const availabilities = VentrataDataProvider.useAvailabilities('optional')

  const usedUnits = units.filter((unit) => unit.id in counters)

  const option = product.options.find((option) => option.id === optionId)
  const optionAvailabilities = availabilities ? availabilities[optionId] || [] : []
  const availability = optionAvailabilities.find((availability) => availability.id === availabilityId)

  const unitsPricing =
    availability?.unitPricing && availability.unitPricing.length > 0
      ? availability.unitPricing.reduce((result: Record<string, number>, unit) => {
          result[unit.unitId] = unit.retail || 0
          return result
        }, {})
      : availability?.pricing
      ? usedUnits.reduce((result: Record<string, number>, unit) => {
          result[unit.id] = availability.pricing?.retail || 0
          return result
        }, {})
      : null

  const total = unitsPricing
    ? usedUnits.reduce((result, unit) => {
        return result + unitsPricing[unit.id] * counters[unit.id]
      }, 0)
    : null

  return (
    <div className={cx('flex flex-col w-full', className)}>
      <div className="flex flex-col">
        {!product.bannerImageUrl && <div className="rounded-t-lg bg-gray-3 w-full h-[128px]" />}
        {product.bannerImageUrl && (
          <div
            className="rounded-t-lg bg-gray-3 w-full h-[128px]"
            style={{
              backgroundImage: `url(${product.bannerImageUrl})`,
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        )}
      </div>
      <div
        className={cx(
          'px-4 flex flex-col divide-solid divide-y divide-gray-2 bg-white rounded-b-lg',
          product.bannerImageUrl ? '' : 'rounded-t-lg'
        )}
      >
        {!option && product.title && (
          <div className="flex flex-col py-4">
            <div className="font-bold">{product.title}</div>
          </div>
        )}

        {option && (
          <div className="flex flex-col py-4">
            <div className="font-bold ">{option.title}</div>
            <div className="flex items-center gap-[6px]">
              <ClockIcon />
              <span>{option.duration}</span>
            </div>
          </div>
        )}

        {date && (
          <div className="flex flex-col py-4 gap-2">
            <div className="font-bold">When</div>
            <div className="flex gap-2">
              <div>{date && moment(date).format('dddd, MMM D, YYYY')}</div>
              <div>{availabilityId && '•'}</div>
              <div>{availabilityId && formatAvailabilityId(availabilityId)}</div>
            </div>
          </div>
        )}

        {usedUnits.length !== 0 && (
          <div className="py-4 flex flex-col gap-4">
            {usedUnits.map((unit) => {
              const counter = counters[unit.id]

              const unitPricing = unitsPricing ? unitsPricing[unit.id] : null

              return (
                <div key={unit.id} className="flex justify-between">
                  <div>
                    {unit.title} x {counter}
                  </div>
                  <div>{unitPricing && formatMoney(unitPricing * counter, currency)}</div>
                </div>
              )
            })}
          </div>
        )}

        {total !== null && (
          <div className="py-4 flex justify-between font-bold">
            <div>Total</div>
            <div>{formatMoney(total, currency)}</div>
          </div>
        )}
      </div>
    </div>
  )
}
